import { m, useMotionValue, useTransform } from 'motion/react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { postAttempt, type Question } from '@/api';
import { afterSettle } from '@/afterSettle';
import { useIsGuest } from '@/auth';
import { Glyph } from '@/components/icons';
import { MainAction } from '@/components/MainAction';
import { QuestionImage } from '@/components/QuestionImage';
import { TipPill, Toast } from '@/components/Toast';
import { QBLEED, QTEXT, SCREEN } from '@/components/layout';
import { focusOnMount } from '@/components/option';
import { canSwipe, EASE_SLIDE, SWIPE, swipeCommitted, swipeOpacity } from '@/gestures';
import { prefersReducedMotion, usePrefetchImage } from '@/hooks';
import { useCovered } from '@/covered';
import { useQuestionFocus } from '@/focusMode';
import { cn } from '@/lib/utils';
import { haptic } from '@/platform';
import { GapSheet, PracticeResult } from '@/pages/practice/EndSheets';
import { showMini } from '@/pages/practice/explain/format';
import { MiniPlayer } from '@/pages/practice/explain/MiniPlayer';
import { useSilentAudio } from '@/pages/practice/explain/silentAudio';
import { useExplainAudio } from '@/pages/practice/explain/useExplainAudio';
import { Loader } from '@/components/Loader';
import { JumpStrip } from '@/pages/practice/JumpStrip';
import { QuestionOptions } from '@/pages/practice/QuestionOptions';
import { LearnButton, VerdictBlock } from '@/pages/practice/VerdictBlock';
import { SHOW_MEDIA } from '@/pro';
import { useSaved } from '@/saved';
import { readStored, writeStored } from '@/storage';
import { plural, type Strings, topicName } from '@/str';
import { useMotion } from '@/motionLoad';

const useAudio = SHOW_MEDIA ? useExplainAudio : useSilentAudio;

const AUTO_ADVANCE_MS = 600;
const TOAST_MS = 4000;
const TIP_MS = 4500;
const TIP_KEY = 'swipe_tip_seen';

type ReportSheetType = typeof import('@/components/ReportSheet').ReportSheet;
type ExplainSheetType = typeof import('@/pages/practice/ExplainSheet').ExplainSheet;

let explainSheet: ExplainSheetType | null = null;

function loadExplainSheet(): Promise<ExplainSheetType | null> {
  if (explainSheet) return Promise.resolve(explainSheet);
  return import('@/pages/practice/ExplainSheet').then(
    (m) => (explainSheet = m.ExplainSheet),
    () => null,
  );
}

let reportSheet: ReportSheetType | null = null;

function loadReportSheet(): Promise<ReportSheetType | null> {
  if (reportSheet) return Promise.resolve(reportSheet);
  return import('@/components/ReportSheet').then(
    (m) => (reportSheet = m.ReportSheet),
    () => null,
  );
}

function play(el: Element | null | undefined, keyframes: Keyframe[], ms: number, delay = 0) {
  if (!el || prefersReducedMotion() || typeof (el as HTMLElement).animate !== 'function') return;
  (el as HTMLElement).animate(keyframes, {
    duration: ms,
    delay,
    easing: 'cubic-bezier(.22,1,.36,1)',
  });
}

export interface SessionResult {
  marks: (boolean | null)[];
  correct: number;
  wrong: number;
}

export function Session({
  questions,
  t,
  onDone,
  onMore,
  onProgress,
  onFinish,
  onResult,
  intro,
  label,
  doneLabel,
  summary = true,
  start = 0,
  gapCheck = true,
}: {
  questions: readonly (Question | undefined)[];
  t: Strings;
  onDone: () => void;
  onMore?: () => void;
  onProgress?: (index: number) => void;
  onFinish?: () => void;
  onResult?: (result: SessionResult) => void;
  intro?: ReactNode;
  label?: string;
  doneLabel?: string;
  summary?: boolean;
  start?: number;
  gapCheck?: boolean;
}) {
  const [index, setIndex] = useState(start);
  const indexRef = useRef(start);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [sheet, setSheet] = useState(false);
  const [Explain, setExplain] = useState<ExplainSheetType | null>(() => explainSheet);
  const [reporting, setReporting] = useState(false);
  const [Report, setReport] = useState<ReportSheetType | null>(() => reportSheet);
  const [gaps, setGaps] = useState<{ count: number; first: number } | null>(null);
  const [result, setResult] = useState<{ correct: number; total: number } | null>(null);
  const [resultOpen, setResultOpen] = useState(false);
  const [bmToast, setBmToast] = useState<{ id: string; on: boolean } | null>(null);
  const [tip, setTip] = useState(false);

  useEffect(() => afterSettle(() => void loadReportSheet()), []);
  useMotion();
  const saved = useSaved();
  const guest = useIsGuest();
  const audio = useAudio();
  const covered = useCovered();
  const picks = useRef<Record<number, number>>({});
  const autoTimer = useRef<number | null>(null);
  const toastTimer = useRef<number | null>(null);
  const plates = useRef<HTMLDivElement>(null);
  const star = useRef<HTMLSpanElement>(null);
  const [autoPending, setAutoPending] = useState(false);
  const [done, setDone] = useState(false);

  const cancelAuto = useCallback(() => {
    if (autoTimer.current !== null) {
      clearTimeout(autoTimer.current);
      autoTimer.current = null;
    }
    setAutoPending(false);
  }, []);

  useEffect(() => cancelAuto, [cancelAuto]);
  useEffect(
    () => () => {
      if (toastTimer.current !== null) clearTimeout(toastTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (index !== 1 || readStored(TIP_KEY)) return;
    writeStored(TIP_KEY, '1');
    const show = window.setTimeout(() => setTip(true), 0);
    const hide = window.setTimeout(() => setTip(false), TIP_MS);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
      setTip(false);
    };
  }, [index]);

  const q = questions[index];
  const selected = answers[index] ?? null;
  const answered = selected !== null;
  const isLast = index === questions.length - 1;
  const answeredCount = Object.keys(answers).length;
  useQuestionFocus(
    label,
    label === undefined
      ? undefined
      : `${answeredCount} / ${questions.length} ${plural(questions.length, t.savolUnit)}`,
  );
  const pickedCorrect =
    answered && (q?.answers.find((a) => a.answer_index === selected)?.is_correct ?? false);

  usePrefetchImage(questions[index + 1]?.image_url ?? null);

  useEffect(() => {
    if (answered) void loadExplainSheet();
  }, [answered]);

  function openExplain(at = index) {
    void loadExplainSheet().then((sheet) => {
      if (!sheet) {
        haptic.notify('error');
        return;
      }
      setExplain(() => sheet);
      if (indexRef.current === at) setSheet(true);
    });
  }

  function choose(answerIndex: number) {
    if (!q || answered || dragged.current) return;
    const correct = q.answers.find((a) => a.answer_index === answerIndex)?.is_correct ?? false;
    picks.current = { ...picks.current, [index]: answerIndex };
    setAnswers(picks.current);

    const opts = plates.current?.querySelectorAll('[data-opt]');
    const pos = q.answers.findIndex((a) => a.answer_index === answerIndex);
    const right = q.answers.findIndex((a) => a.is_correct);
    if (correct) {
      haptic.notify('success');
      play(
        opts?.[pos],
        [{ transform: 'scale(.96)' }, { transform: 'scale(1.025)' }, { transform: 'none' }],
        280,
      );
    } else {
      haptic.notify('error');
      play(
        opts?.[pos],
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(-6px)' },
          { transform: 'translateX(5px)' },
          { transform: 'translateX(-3px)' },
          { transform: 'translateX(0)' },
        ],
        280,
      );
      play(
        opts?.[right],
        [{ transform: 'none' }, { transform: 'scale(1.02)' }, { transform: 'none' }],
        320,
        180,
      );
    }

    if (correct && !isLast) {
      setAutoPending(true);
      autoTimer.current = window.setTimeout(() => {
        autoTimer.current = null;
        setAutoPending(false);
        jump(index + 1);
      }, AUTO_ADVANCE_MS);
    }
    void postAttempt({
      question_id: q.id,
      selected_answer_index: answerIndex,
      mode: 'practice',
    }).catch(() => {});
  }

  function marks() {
    return questions.map((question, i) => {
      const pick = picks.current[i];
      if (pick === undefined) return null;
      return question?.answers.find((a) => a.answer_index === pick)?.is_correct ?? false;
    });
  }

  function finish() {
    setDone(true);
    const m = marks();
    const correct = m.filter((x) => x === true).length;
    const wrong = m.filter((x) => x === false).length;
    onResult?.({ marks: m, correct, wrong });
    onFinish?.();
    if (onResult) return;
    if (!summary) return onDone();
    setResult({ correct, total: gapCheck ? questions.length : correct + wrong });
    setResultOpen(true);
  }

  function next() {
    if (!isLast) return jump(index + 1);
    const open = gapCheck
      ? questions.map((_, i) => i).filter((i) => picks.current[i] === undefined)
      : [];
    if (open.length > 0) return setGaps({ count: open.length, first: open[0] });
    finish();
  }

  function again() {
    setResult(null);
    setResultOpen(false);
    if (onMore) return onMore();
    audio.reset();
    picks.current = {};
    setAnswers({});
    setDone(false);
    jump(0);
  }

  function jump(i: number, slide = true) {
    cancelAuto();
    setEnterFrom(slide ? Math.sign(i - index) : 0);
    setIndex(i);
    onProgress?.(i);
    setSheet(false);
  }

  function toggleBookmark() {
    if (!q) return;
    cancelAuto();
    const on = !saved.has(q.id);
    saved.toggle(q.id);
    play(
      star.current,
      [{ transform: 'scale(1)' }, { transform: 'scale(1.28)' }, { transform: 'scale(1)' }],
      220,
    );
    setBmToast({ id: q.id, on });
    if (toastTimer.current !== null) clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setBmToast(null), TOAST_MS);
  }

  const reduced = prefersReducedMotion();
  const x = useMotionValue(0);
  const dragOpacity = useTransform(x, swipeOpacity);
  const [enterFrom, setEnterFrom] = useState(0);
  const dragged = useRef(false);
  const leaving = useRef<{ frame: number; finish: () => void } | null>(null);

  useEffect(() => () => cancelAnimationFrame(leaving.current?.frame ?? 0), []);

  function slideOut(dir: -1 | 1, then: () => void) {
    const from = x.get();
    const to = -dir * window.innerWidth * SWIPE.exitShare;
    const start = performance.now();
    const finish = () => {
      cancelAnimationFrame(leaving.current?.frame ?? 0);
      leaving.current = null;
      then();
    };
    const step = (now: number) => {
      if (x.isAnimating()) x.stop();
      const p = Math.min(1, (now - start) / SWIPE.exitMs);
      x.set(from + (to - from) * (1 - (1 - p) * (1 - p)));
      if (p < 1 && leaving.current) leaving.current.frame = requestAnimationFrame(step);
      else if (leaving.current) finish();
    };
    leaving.current = { frame: requestAnimationFrame(step), finish };
  }

  function commitSwipe(dir: -1 | 1) {
    if (!canSwipe(dir, index, questions.length)) return false;
    cancelAuto();
    haptic.selection();
    const to = index + dir;
    slideOut(dir, () => {
      jump(to);
      x.set(0);
    });
    return true;
  }

  if (index >= questions.length) return null;

  const isSaved = q !== undefined && saved.has(q.id);

  return (
    <div data-screen className={cn(SCREEN, 'min-h-dvh gap-2.75 pt-2.5')}>
      {intro}
      <JumpStrip questions={questions} index={index} answers={answers} onJump={jump} t={t} />

      <m.div
        key={index}
        drag={reduced ? false : 'x'}
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={SWIPE.elastic}
        dragMomentum={false}
        className="flex grow touch-pan-y flex-col gap-2.75"
        style={{ x, opacity: reduced ? 1 : dragOpacity }}
        onDragStart={() => {
          dragged.current = true;
          leaving.current?.finish();
        }}
        onDragEnd={(_, info) => {
          const dx = info.offset.x;
          if (swipeCommitted(dx, info.velocity.x)) commitSwipe(dx < 0 ? 1 : -1);
          setTimeout(() => {
            dragged.current = false;
          }, 0);
        }}
        initial={reduced || enterFrom === 0 ? false : { x: enterFrom * SWIPE.enterPx, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: SWIPE.enterSeconds, ease: EASE_SLIDE }}
        data-enter={enterFrom}
      >
        {q === undefined ? (
          <div data-slot-loading>
            <Loader />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1.5" ref={plates}>
              <div className={cn(QBLEED, 'flex items-center justify-between gap-2.5')}>
                <div className="text-t11 text-faint tracking-kicker min-w-0 truncate uppercase">
                  {topicName(q.topic, t) !== label && topicName(q.topic, t)}
                </div>
                <div className="relative flex flex-none items-center gap-2.5">
                  <div className="text-t12 text-muted-foreground font-semibold whitespace-nowrap">
                    {index + 1} / {questions.length}
                  </div>
                  {!guest && (
                    <button
                      type="button"
                      onClick={() => {
                        cancelAuto();
                        void loadReportSheet().then((sheet) => {
                          if (!sheet) {
                            haptic.notify('error');
                            return;
                          }
                          setReport(() => sheet);
                          setReporting(true);
                        });
                      }}
                      title={t.report}
                      aria-label={t.report}
                      className="text-faint hover:text-muted-foreground -my-3 -mr-1 flex min-h-11 min-w-11 items-center justify-end"
                    >
                      <Glyph name="flag" size={20} weight={2.1} strokeLinejoin={undefined} />
                    </button>
                  )}
                  {!guest && (
                    <button
                      type="button"
                      onClick={toggleBookmark}
                      title={isSaved ? t.unsave : t.save}
                      aria-label={t.save}
                      aria-pressed={isSaved}
                      className={cn(
                        'text-t24 -my-3 flex min-h-11 min-w-11 items-center justify-end leading-none',
                        isSaved ? 'text-accent-ink' : 'text-faint',
                      )}
                    >
                      <span ref={star} data-star className="block">
                        {isSaved ? '★' : '☆'}
                      </span>
                    </button>
                  )}
                  {!guest && saved.failed === q.id && (
                    <span
                      role="alert"
                      className="rounded-ctl border-border bg-card text-bad text-t12 absolute top-full right-0 z-10 mt-2 max-w-60 border px-3 py-2 font-semibold"
                    >
                      {t.saveFailed}
                    </span>
                  )}
                </div>
              </div>

              <h1
                className={cn(QTEXT, QBLEED, 'outline-none')}
                key={q.id}
                tabIndex={-1}
                ref={focusOnMount}
              >
                {q.question_text ?? t.savol}
              </h1>

              {q.image_url && (
                <div className="my-1">
                  <QuestionImage key={q.image_url} src={q.image_url} bleed />
                </div>
              )}

              <QuestionOptions
                answers={q.answers}
                answered={answered}
                selected={selected}
                onChoose={choose}
                label={t.savol}
              />

              {!answered && (
                <div className="flex justify-end pt-1">
                  <LearnButton
                    t={t}
                    quiet
                    onClick={() => {
                      cancelAuto();
                      openExplain();
                    }}
                  />
                </div>
              )}
              {answered && (
                <VerdictBlock
                  pickedCorrect={pickedCorrect}
                  t={t}
                  onShowExplain={() => {
                    cancelAuto();
                    if (audio.track && audio.track.index !== index) audio.reset();
                    openExplain();
                  }}
                />
              )}
              <div className="h-1" />
            </div>
          </>
        )}
      </m.div>

      {sheet && Explain && q && !covered && (
        <Explain question={q} index={index} audio={audio} t={t} onClose={() => setSheet(false)} />
      )}

      {SHOW_MEDIA && showMini(audio, sheet) && (
        <MiniPlayer
          audio={audio}
          t={t}
          onOpen={() => {
            const at = audio.track?.index ?? index;
            if (at !== index) {
              jump(at, false);
            }
            openExplain(at);
          }}
        />
      )}

      {reporting && Report && q && (
        <Report key={q.id} questionId={q.id} onClose={() => setReporting(false)} />
      )}

      {gaps && (
        <GapSheet
          count={gaps.count}
          t={t}
          onGo={() => {
            const first = gaps.first;
            setGaps(null);
            jump(first);
          }}
          onFinish={() => {
            setGaps(null);
            finish();
          }}
          onClose={() => setGaps(null)}
        />
      )}

      {result && resultOpen && (
        <PracticeResult
          correct={result.correct}
          total={result.total}
          t={t}
          doneLabel={doneLabel}
          onAgain={again}
          onHome={() => {
            setResultOpen(false);
            onDone();
          }}
          onClose={() => setResultOpen(false)}
        />
      )}

      {bmToast && saved.failed !== bmToast.id && (
        <Toast
          glyph={
            <span className={bmToast.on ? 'text-accent-ink' : 'text-faint'}>
              {bmToast.on ? '★' : '☆'}
            </span>
          }
          action={t.exp.undo}
          onAction={() => {
            if (saved.has(bmToast.id) === bmToast.on) saved.toggle(bmToast.id);
            setBmToast(null);
          }}
        >
          {bmToast.on ? t.exp.saved : t.exp.unsaved}
        </Toast>
      )}

      {tip && !bmToast && <TipPill glyph="⇆">{t.exp.swipeTip}</TipPill>}

      <MainAction
        text={isLast || done ? t.finish : t.next}
        visible={
          done
            ? result !== null && !resultOpen && !reporting && !sheet
            : (answered || (isLast && questions.length > 1)) && !reporting && !autoPending && !sheet
        }
        onClick={done ? () => setResultOpen(true) : next}
      />
    </div>
  );
}
